import type { ContactsSync } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { del, errorMessage, get, post, put } from "../api";
import { Alert, Button, Checkbox, Loading, Panel, PanelHeader, cx, formatDateTime } from "./ui";

/** My profile → Contacts on your phone: the CRM's contacts in the phone's own Contacts (CardDAV, read-only). */
export function ContactsSyncCard() {
  const qc = useQueryClient();
  const sync = useQuery({ queryKey: ["contacts-sync"], queryFn: () => get<ContactsSync>("/api/me/contacts-sync") });
  const [password, setPassword] = useState<string | null>(null);
  const [how, setHow] = useState<"iphone" | "android" | "mac">("iphone");
  const done = (s: ContactsSync) => qc.setQueryData(["contacts-sync"], s);
  const make = useMutation({
    mutationFn: () => post<{ password: string; sync: ContactsSync }>("/api/me/contacts-sync/password", {}),
    onSuccess: (r) => (setPassword(r.password), done(r.sync)),
  });
  const off = useMutation({ mutationFn: () => del<ContactsSync>("/api/me/contacts-sync/password"), onSuccess: (s) => (setPassword(null), done(s)) });
  const types = useMutation({ mutationFn: (typeIds: string[] | null) => put<ContactsSync>("/api/me/contacts-sync/types", { typeIds }), onSuccess: done });
  const s = sync.data;
  const error = make.error ?? off.error ?? types.error ?? sync.error;

  const toggle = (id: string, on: boolean) => {
    if (!s) return;
    const next = s.types.filter((t) => (t.id === id ? on : t.on)).map((t) => t.id);
    types.mutate(next.length === s.types.length ? null : next);
  };

  return (
    <Panel>
      <PanelHeader title="Contacts on your phone" description="CRM contacts in your phone's own Contacts, so calls and texts show who it is. Read-only there: change contacts in the CRM. Works with iPhone, Mac, Android (DAVx⁵), and Thunderbird." />
      <div className="flex flex-col gap-4 px-5 py-5">
        {sync.isLoading || !s ? (
          <Loading />
        ) : (
          <>
            {password ? (
              <Alert tone="ok">
                Your contacts password: <span className="font-mono text-base font-bold tracking-wide select-all">{password}</span>
                <br />
                Copy it into your phone now; it isn't shown again (you can make a new one).
              </Alert>
            ) : null}
            {s.on ? (
              <div className="grid gap-1 text-sm sm:grid-cols-[8rem_1fr]">
                <span className="text-muted">Server</span>
                <span className="font-mono select-all">{s.server}</span>
                <span className="text-muted">User name</span>
                <span className="font-mono select-all">{s.username}</span>
                <span className="text-muted">Password</span>
                <span>{password ? "Above" : "The one you made (make a new one if you don't have it)"}</span>
                <span className="text-muted">Full address</span>
                <span className="font-mono break-all select-all">{s.url}</span>
                <span className="text-muted">Last synced</span>
                <span>{s.lastUsedAt ? `${formatDateTime(s.lastUsedAt)}${s.device ? ` · ${s.device.slice(0, 60)}` : ""}` : "Not yet"}</span>
              </div>
            ) : (
              <p className="text-sm text-ink-soft">Off. Make a password to set up your phone; it's only for this (not your Google sign-in).</p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button variant={s.on ? "secondary" : "primary"} busy={make.isPending} onClick={() => make.mutate()}>
                {s.on ? "Make a new password" : "Make a password"}
              </Button>
              {s.on && (
                <Button busy={off.isPending} onClick={() => off.mutate()}>
                  Turn off
                </Button>
              )}
            </div>
            {s.on && <p className="text-xs text-muted">A new password stops the old one (every phone using it needs the new one). Turning off removes it; phones then drop the contacts on their next sync or show a sign-in error.</p>}

            <div>
              <h3 className="mb-1 text-sm font-semibold text-ink-soft">Which contacts</h3>
              <div className="grid sm:grid-cols-2">
                {s.types.map((t) => (
                  <Checkbox key={t.id} label={`${t.name} (${t.count})`} checked={t.on} disabled={types.isPending} onChange={(v) => toggle(t.id, v)} />
                ))}
              </div>
            </div>

            {s.recent.length > 0 && (
              <details className="rounded-[var(--radius-control)] bg-mist/60 px-3 py-2">
                <summary className="cursor-pointer text-sm font-semibold text-ink-soft">What your phone asked ({s.recent.length})</summary>
                <p className="mt-1 text-xs text-muted">The last requests your phone made (newest first), to see where syncing gets stuck. No contact details are kept.</p>
                <ol className="mt-2 flex flex-col gap-1.5 font-mono text-[11px] leading-snug">
                  {s.recent.map((r, i) => (
                    <li key={i} className={cx("rounded bg-white px-2 py-1", r.status >= 400 && "text-danger")}>
                      {new Date(r.at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" })} · {r.method} {r.path}
                      {r.depth !== null ? ` · depth ${r.depth}` : ""} · {r.status}
                      {r.kind ? ` · ${r.kind}` : ""}
                      {r.items !== null ? ` · ${r.items} items` : ""}
                      {r.note ? ` · ${r.note}` : ""}
                      {r.props.length > 0 && <span className="block break-words text-muted">asked: {r.props.join(", ")}</span>}
                    </li>
                  ))}
                </ol>
              </details>
            )}

            <div>
              <div className="mb-2 flex gap-1 rounded-[var(--radius-control)] bg-mist p-0.5 text-sm sm:w-fit">
                {(
                  [
                    ["iphone", "iPhone"],
                    ["mac", "Mac"],
                    ["android", "Android"],
                  ] as const
                ).map(([k, label]) => (
                  <button key={k} className={cx("flex-1 rounded px-3 py-1.5 font-medium", how === k ? "bg-white shadow-sm" : "text-muted")} onClick={() => setHow(k)}>
                    {label}
                  </button>
                ))}
              </div>
              <ol className="list-decimal pl-5 text-sm text-ink-soft">
                {how === "iphone" && (
                  <>
                    <li>Settings → Apps → Contacts → Contacts Accounts → Add Account → Other → Add CardDAV Account.</li>
                    <li>Server: {s.server} · User name: {s.username} · Password: your contacts password. Tap Next.</li>
                    <li>The contacts show in Contacts under "NMT CRM", grouped by type. Calls and texts from them show their name.</li>
                    <li>To see only some groups: Contacts → Lists.</li>
                  </>
                )}
                {how === "mac" && (
                  <>
                    <li>Contacts → Settings → Accounts → + → Other Contacts Account.</li>
                    <li>Type CardDAV, Manual: user name {s.username}, password your contacts password, server {s.server}.</li>
                  </>
                )}
                {how === "android" && (
                  <>
                    <li>Install DAVx⁵ (Play Store or F-Droid) → + → Login with URL and user name.</li>
                    <li>Base URL: {s.url} · User name: {s.username} · Password: your contacts password.</li>
                    <li>Tick the "NMT CRM" address book. In DAVx⁵'s account settings, "Contact group method: Groups are separate vCards" shows the type groups.</li>
                  </>
                )}
              </ol>
            </div>
          </>
        )}
        {error && <Alert>{errorMessage(error)}</Alert>}
      </div>
    </Panel>
  );
}
